/**
 * The enquiry list, from the server.
 *
 * The search box and the page are the server's — `GET /api/v1/inquiries/`
 * takes both — so typing or turning a page refetches rather than narrowing
 * what is already on screen: the list is paginated, and filtering a page in
 * the browser would only ever search the rows that page happened to hold.
 *
 * Laid over what comes back is whatever this sitting has done that the backend
 * has not been told about — a stage moved here, which would otherwise read as
 * the stage the row was fetched at. See `enquiriesService`.
 *
 * Which page is the caller's to hold, not this hook's: it is the thing a
 * screen has to reset when the search changes, and the hook has no way of
 * knowing that a keystroke came from the user rather than from a re-render.
 */

import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { ApiError } from "../../../lib/http";
import { useTokenGetter } from "../../auth/hooks/useTokenGetter";
import {
  DEFAULT_PAGE_SIZE,
  fetchEnquiriesPage,
  getEnquiryOverrides,
  subscribeToEnquiries,
} from "../services/enquiriesService";
import type { Enquiry } from "../types/enquiries.types";

/** Long enough that typing a name is one request, not eight. */
const SEARCH_DEBOUNCE_MS = 300;

export type EnquiriesStatus = "loading" | "ready" | "failed";

export interface EnquiriesState {
  /**
   * The rows in hand: one page of them by default, or every page fetched so
   * far when the caller is accumulating — see `accumulate`.
   */
  enquiries: Enquiry[];
  /** How many the server holds for this search, not how many came back. */
  count: number;
  status: EnquiriesStatus;
  /** Set only when `status` is `failed` — what the screen shows. */
  error?: string;
  /** Runs the call again. For a Try again button, not for every keystroke. */
  reload: () => void;
  /** Whether the server has pages past the ones handed over. */
  hasMore: boolean;
  /**
   * Whether one of those is on the wire.
   *
   * Not a load anybody is waiting on — the rows already fetched are on screen
   * — so it is what lets a list say "still coming" at its end rather than
   * looking like the end of the list.
   */
  loadingMore: boolean;
  /**
   * Asks for the next page. Safe to call twice for the same one: a list asks
   * as its end comes into view, which can happen on consecutive scroll events.
   * Does nothing unless `accumulate` is on.
   */
  loadMore: () => void;
}

export interface UseEnquiriesOptions {
  /** Debounced here, so a call site can pass the box's value directly. */
  search?: string;
  /** 1-based, the way the endpoint counts. Changing it refetches. */
  page?: number;
  /** How many rows a page holds. Defaults to the service's ten. */
  pageSize?: number;
  /**
   * Keep the pages rather than replacing them, which is how a phone walks this
   * list: there are no pages there, only scrolling, so each one fetched is
   * added under the last and `loadMore` asks for the next.
   *
   * `page` is ignored while this is on — the feed always starts at one, and
   * the reader's place in it is how far they have scrolled rather than a
   * number the screen is holding. Changing the search starts it over, which
   * falls out of the query key rather than needing to be remembered.
   */
  accumulate?: boolean;

  /**
   * A stage to leave out — the endpoint's `notinclude`. Part of the cache key
   * below, so a list asked for without the converted ones is a different
   * question from the whole pipeline rather than the same one answered twice.
   */
  notInclude?: string;
}

const failureMessage = (error: unknown) =>
  error instanceof ApiError
    ? error.message
    : "We could not reach the server. Check your connection and try again.";

const NO_ENQUIRIES: Enquiry[] = [];

export function useEnquiries({
  search = "",
  page = 1,
  pageSize = DEFAULT_PAGE_SIZE,
  accumulate = false,
  notInclude,
}: UseEnquiriesOptions = {}): EnquiriesState {
  const getToken = useTokenGetter();

  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const isFirstSearch = useRef(true);

  useEffect(() => {
    // The first pass must not wait — the screen would otherwise open on a
    // blank list for no reason, since there is nothing typed to debounce.
    if (isFirstSearch.current) {
      isFirstSearch.current = false;
      setDebouncedSearch(search);
      return;
    }

    const timer = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [search]);

  /**
   * One query serving both ways of reading the list.
   *
   * An infinite query even for the paged screen, where it simply never fetches
   * a second page: `initialPageParam` is the page being asked for and
   * `getNextPageParam` answers nothing, so `pages` holds exactly the one page
   * the table wants. The alternative — two hooks, chosen by width — would
   * either break the rules of hooks or put two lists of the same rows on the
   * wire.
   *
   * The two modes are kept in separate caches by the last key segment: a feed
   * that has scrolled to page four and a table sitting on page one are
   * different answers to the same question, and one must not be served the
   * other's rows on a resize.
   */
  const query = useInfiniteQuery({
    queryKey: [
      "enquiries",
      "list",
      debouncedSearch,
      pageSize,
      accumulate ? "feed" : page,
      notInclude ?? "",
    ] as const,
    queryFn: ({ pageParam, signal }) =>
      fetchEnquiriesPage(getToken, {
        search: debouncedSearch,
        page: pageParam,
        pageSize,
        notInclude,
        signal,
      }),
    initialPageParam: accumulate ? 1 : page,
    getNextPageParam: (last, _pages, lastParam) =>
      accumulate && last.hasNext ? lastParam + 1 : undefined,
    // The rows already on screen stay while the next set is fetched — cached
    // across mounts too, so a list visited again shows its last page instantly
    // while a fresh read runs behind it.
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    if (query.isError) console.error("Enquiries failed to load:", query.error);
  }, [query.isError, query.error]);

  const overrides = useSyncExternalStore(
    subscribeToEnquiries,
    getEnquiryOverrides,
    getEnquiryOverrides
  );

  const pages = query.data?.pages;

  // Back to nothing on a real failure, same as before: a row from a page that
  // is no longer confirmed current is worse than none. Flattened, which is one
  // page for the table and however many have been scrolled to for the feed.
  const fetched = useMemo(
    () =>
      query.isError || !pages
        ? NO_ENQUIRIES
        : pages.flatMap((one) => one.enquiries),
    [query.isError, pages]
  );

  // The payment comes over with the stage: a request answered from the table is
  // settled on the server, but the row it was answered from was fetched before
  // that happened, and would otherwise go on offering a tick and a cross
  // against a payment that is already in.
  const enquiries = useMemo(
    () =>
      fetched.map((row) => {
        const held = overrides[row.id];
        if (!held) return row;

        return {
          ...row,
          status: held.status ?? row.status,
          statusLabel: held.statusLabel ?? row.statusLabel,
          payment: held.payment ?? row.payment,
        };
      }),
    [overrides, fetched]
  );

  const { hasNextPage, isFetchingNextPage, fetchNextPage } = query;

  return {
    enquiries,
    // Every page carries the same total, so the first one answers for the lot.
    count: query.isError ? 0 : pages?.[0]?.count ?? 0,
    status: query.isError
      ? "failed"
      : // A page arriving under the rows already on screen is not the list
        // loading: reporting it as such would shimmer everything the reader is
        // part way through, every time they neared the bottom of it.
        query.isFetching && !isFetchingNextPage
        ? "loading"
        : "ready",
    error: query.isError ? failureMessage(query.error) : undefined,
    reload: () => {
      void query.refetch();
    },
    hasMore: !!hasNextPage,
    loadingMore: isFetchingNextPage,
    loadMore: () => {
      if (hasNextPage && !isFetchingNextPage) void fetchNextPage();
    },
  };
}
